import Notifications from 'react-notification-system-redux';
import { api } from '../../../../general/apiScenarios';
import { CreativeType, INode, NodeFlag } from '../../../../general/model';
import { CHNode, CHNodeIsCreativeFolder, INodeToCHNode } from '../../../../general/node';
import { Interrupt, TSagaReturn } from '../../../../general/scenarioFactory';
import { ListenerApiLight } from '../../../listenerMiddleware';
import { CreativeBLSettings, makeCreative, Messages } from '../legacy_models/model';
import { spacesToDashes } from '../../creativeFolder/model';

/**
 * As if sagas could be pure.
 *
 * these sagas adhere to the new paradigm:
 * - only `call` other pure or API sagas
 * - don't `select` anything
 * - don't `take` anything
 * - don't `put` anything that affects store:
 *   - you CAN `call` an API saga that logs itself via `put`
 *   - you CAN `put` a Notification action
 * - return results must be of `TSagaReturn` type
 *
 */

async function loadBLInfo(
  lapi: ListenerApiLight,
  parentNodeId: number,
): Promise<TSagaReturn<Pick<CreativeBLSettings, 'creative' | 'project' | 'type'>>> {
  try {
    const results = await Promise.all([
      api.bl.loadBLProjects(),
      api.bl.loadCreativeTypes(),
      api.bl.loadCreativeData(parentNodeId),
    ]);
    lapi.signal.throwIfAborted();
    const [projects, types, creatives] = results;
    if (!projects.success || !types.success || !creatives.success) throw new Error();

    const creative = creatives.data.response[0];
    const project = projects.data.response.find((p: any) => p.id === creative.project_id);
    const type = types.data.response.find((t: any) => t.id === creative.type_id);
    if (project === undefined || type === undefined) {
      return {
        success: false,
        args: { parentNodeId },
        reason: Interrupt.Exception(`project or type is undefined`),
      };
    }
    return {
      success: true,
      data: { creative, project, type },
      args: { parentNodeId },
    };
  } catch (x) {
    lapi.dispatch(Notifications.error(Messages.loadBLInfoError()));
    return { success: false, args: { parentNodeId }, reason: Interrupt.API };
  }
}

async function getBreadcrumbsContext(lapi: ListenerApiLight, nodeID: number) {
  const breadcrumbsResult = await api.fs.listParents(nodeID);
  lapi.signal.throwIfAborted();
  if (!breadcrumbsResult.success) throw Interrupt.Exception("Can't load folder parents");
  return breadcrumbsResult.data.response.nodes;
}
async function findCreativeNode(lapi: ListenerApiLight, nodeID: number) {
  const inodes = await getBreadcrumbsContext(lapi, nodeID);
  lapi.signal.throwIfAborted();
  const creativeINode = inodes.find(CHNodeIsCreativeFolder);
  if (creativeINode === undefined)
    throw Interrupt.Exception("Can't find creative among folder hierarcy");
  return INodeToCHNode(creativeINode);
}

export function detectBLContext(lapi: ListenerApiLight, nodeID: number) {
  async function getCreativeProject(breadcrumbs: INode[]) {
    const creativeProjectNode = breadcrumbs.find((n) => n.flags.includes(NodeFlag.folder_project));
    if (creativeProjectNode === undefined) throw Interrupt.Exception('Can not find project folder');

    const projectsResult = await api.bl.loadBLProjects();
    lapi.signal.throwIfAborted();
    if (!projectsResult.success) throw Interrupt.Exception('Can not load projects');

    const project = projectsResult.data.response.find(
      (x) => x.name.toLocaleLowerCase() === creativeProjectNode.name.toLocaleLowerCase(),
    );
    if (project === undefined)
      throw Interrupt.Exception(`Project "${creativeProjectNode.name}" does not exist`);
    return project;
  }

  async function getCreativeType(breadcrumbs: INode[]) {
    const creativeTypeNode = breadcrumbs.find((n) =>
      n.flags.includes(NodeFlag.folder_creative_type),
    );

    if (creativeTypeNode === undefined) throw Interrupt.Exception('Can not find type folder');

    // загрузка типов
    const typesResult = await api.bl.loadCreativeTypes();
    lapi.signal.throwIfAborted();
    if (!typesResult.success) throw Interrupt.Exception('Can not load types');

    // поиск типа в загруженных
    const type = typesResult.data.response.find(
      (x) => x.name.toLocaleLowerCase() === creativeTypeNode.name.toLocaleLowerCase(),
    );
    if (type === undefined)
      throw Interrupt.Exception(`Type "${creativeTypeNode.name}" does not exist`);

    return type;
  }

  async function getDictionaries(type: CreativeType) {
    const dictResult = await api.bl.loadDictionaries(type.id);
    lapi.signal.throwIfAborted();
    if (!dictResult.success) throw Interrupt.Exception('Can not load masks');

    return dictResult.data.response.mask;
  }

  const getNewCreativeName = (breadcrumbs: INode[]) => {
    const nodeName = breadcrumbs[breadcrumbs.length - 1].name;
    const newCreativeName = spacesToDashes(nodeName);
    return newCreativeName;
  };

  return {
    all: async function () {
      const context_breadcrumbs = await getBreadcrumbsContext(lapi, nodeID);
      const project = await getCreativeProject(context_breadcrumbs);
      const type = await getCreativeType(context_breadcrumbs);
      const dictionaries = await getDictionaries(type);
      const newCreativeName = getNewCreativeName(context_breadcrumbs);
      const settings: CreativeBLSettings = {
        creative: makeCreative({
          node_id: nodeID,
          node_name: newCreativeName,
          project_id: project.id,
          type_id: type.id,
        }),

        dictionaries,
        project,
        type,
      };
      return settings;
    },
    project: async function () {
      const context_breadcrumbs = await getBreadcrumbsContext(lapi, nodeID);
      const project = await getCreativeProject(context_breadcrumbs);
      return project;
    },
  };
}

export async function loadBLContext(
  lapi: ListenerApiLight,
  parentNodeId: number,
): Promise<TSagaReturn<CreativeBLSettings>> {
  if (!parentNodeId)
    return {
      success: false,
      args: { parentNodeId },
      reason: Interrupt.Exception('no parentNodeId'),
    };

  const creativeNodeID: CHNode | undefined = await findCreativeNode(lapi, parentNodeId);
  lapi.signal.throwIfAborted();

  if (creativeNodeID === undefined) {
    return {
      success: false,
      args: { parentNodeId },
      reason: Interrupt.Exception('creativeNodeID === undefined'),
    };
  }

  const BLInfo = await loadBLInfo(lapi, creativeNodeID.id);
  lapi.signal.throwIfAborted();

  if (!BLInfo.success) {
    return { success: false, args: { parentNodeId }, reason: Interrupt.API };
  }
  const blType = BLInfo.data.type;
  const dictResult = await api.bl.loadDictionaries(blType.id);
  lapi.signal.throwIfAborted();

  if (!dictResult.success) {
    return { success: false, args: { parentNodeId }, reason: Interrupt.API };
  }

  const result: TSagaReturn<CreativeBLSettings> = {
    success: true,
    // TODO: lang support
    data: { ...BLInfo.data, dictionaries: dictResult.data.response.mask },
    args: { parentNodeId },
  };
  return result;
}

export async function getNodes(lapi: ListenerApiLight, nodeID: number) {
  const nodesResult = await api.fs.loadNodes(nodeID);
  lapi.signal.throwIfAborted();
  if (!nodesResult.success) throw Interrupt.Exception('Can not load folder contents');
  const context_nodes: CHNode[] = nodesResult.data.response.nodes.map(INodeToCHNode);
  return context_nodes;
}
